<template>
  <v-container fluid>
    <section>
      <!-- Summary Cards (Matches /products page card summary) -->
      <KpiCards class="mb-3" :items="kpiItems" :loading="!summaries.length" :skeleton="4" />

      <!-- Orders Table -->
      <data-table
        :headers="headers"
        :items="items"
        commit="SET_ALL_PRODUCTS"
        :viewItem="viewItem"
      >
        <div slot="activator" class="row mb-4">
          <div class="col-12">
            <div class="d-flex align-center justify-space-between flex-wrap gap-3">
              <v-card-title class="py-0 pl-0 mb-0 ps-products-title">
                {{ $t("marketplace_orders") || "Marketplace Orders" }}
              </v-card-title>
              <div class="br-tabs">
                <div
                  v-for="(val, i) in statusList"
                  :key="i"
                  class="br-tab"
                  :class="{ 'br-tab--active': status === val.value }"
                  @click="status = val.value"
                >
                  {{ val.text }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-xs-6 col-sm-6 col-md-3">
            <DatePicker
              :dense="true"
              :placeholder="false"
              label="start_date"
              v-model="start_date"
            />
          </div>
          <div class="col-xs-6 col-sm-6 col-md-3">
            <DatePicker
              :dense="true"
              :placeholder="false"
              label="end_date"
              v-model="end_date"
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4">
            <TextSearch
              v-model="search"
              @search="onSearch()"
              @onClear="
                search = '';
                onSearch();
              "
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-2 text-right">
            <v-btn
              min-width="auto"
              depressed
              outlined
              color="primary"
              class="rounded-lg"
              style="min-height: 40px"
              :loading="exporting"
              @click="exportOrders"
            >
              <v-icon left small>fal fa-file-export</v-icon>
              {{ $t("export") || "Export" }}
            </v-btn>
          </div>
        </div>
      </data-table>

      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import PubNub from "pubnub";
import Help from "@/utils/helper";
import { pubnubConfig } from "@/utils/mock";
import { toast } from "@/controllers/alert";
import TextSearch from "@/components/global/TextSearch";
import DatePicker from "@/components/global/DatePicker";
import DataTable from "@/components/DataTable";
import { mapMutations } from "vuex";
import { myMixin } from "@/mixin/main";

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);
const tomorrow = new Date(
  Date.now() - new Date().getTimezoneOffset() * 60000 + 86400000
)
  .toISOString()
  .substr(0, 10);

export default {
  name: "MarketplaceOrders",
  mixins: [myMixin],
  components: {
    TextSearch,
    DatePicker,
    DataTable,
  },
  data: () => ({
    pubnub: new PubNub(pubnubConfig()),
    overlay: false,
    search: "",
    start_date: today,
    end_date: tomorrow,
    status: "all",
    items: { data: [] },
    summaries: [],
    exporting: false,
  }),
  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        title: item.title,
        icon: item.icon,
        value: item.value ?? 0,
      }));
    },
    statusList() {
      return [
        { text: this.$t("all") || "All", value: "all" },
        { text: this.$t("processing") || "Processing", value: "processing" },
        { text: this.$t("shipping") || "Shipping", value: "shipping" },
        { text: this.$t("completed") || "Completed", value: "completed" },
        { text: this.$t("cancelled") || "Cancelled", value: "cancelled" },
        { text: this.$t("refunded") || "Refunded", value: "refunded" },
      ];
    },
    headers() {
      return [
        { text: "#", value: "index", sortable: false, width: "50px" },
        { text: this.$t("order_id") || "Order ID", value: "txn", sortable: false },
        { text: this.$t("status") || "Status", value: "status", sortable: false },
        { text: this.$t("customer") || "Customer", value: "buyer_user.first_name", sortable: false },
        { text: this.$t("phone_no") || "Phone", value: "buyer_user.phone_no", sortable: false },
        { text: this.$t("payment_method") || "Payment", value: "payment_method", sortable: false },
        { text: this.$t("orders_count") || "Items", value: "orders_count", sortable: false, align: "center" },
        { text: this.$t("total_price") || "Total", value: "total_price", sortable: false, align: "right" },
        { text: this.$t("profit") || "Profit", value: "total_profit", sortable: false, align: "right" },
        { text: this.$t("date") || "Date", value: "created_at", sortable: false },
        { text: this.$t("action") || "Action", value: "actions", sortable: false },
      ];
    },
  },
  mounted() {
    this.pubnub.addListener({
      message: (event) => {
        const msg = event && event.message;
        const ev = msg && msg.event || (typeof msg === "string" ? msg : "");
        if (ev === "marketplace_new_order") {
          toast(this, this.$t("new_order") || "New marketplace order", "success");
          Help.playAudio();
          this.fetchItem();
          return;
        }
        if (ev === "marketplace_order_cancelled") {
          toast(this, this.$t("order_cancelled") || "Marketplace order cancelled", "info");
          this.fetchItem();
          return;
        }
        if (ev === "marketplace_order_completed") {
          toast(this, this.$t("order_completed") || "Marketplace order completed", "success");
          this.fetchItem();
          this.fetchSummary();
          return;
        }
      },
    });
    const channel = `${this.user.stores.code}_marketplace`;
    this.pubnub.subscribe({ channels: [channel] });
    this.fetchItem();
    this.fetchSummary();
  },
  beforeDestroy() {
    try {
      const channel = `${this.user && this.user.stores && this.user.stores.code}_marketplace`;
      if (channel) this.pubnub.unsubscribe({ channels: [channel] });
    } catch (_) {
      // ignore
    }
  },
  methods: {
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),
    async fetchItem() {
      try {
        this.overlay = true;
        const param = `q=${this.search}&status=${this.status}&start_date=${this.start_date}&end_date=${this.end_date}`;
        const { data } = await this.$http.get(`/get_orders_dash?${param}`);
        this.items = data && data.data || { data: [] };
      } catch (error) {
        console.log(error);
      } finally {
        this.overlay = false;
      }
    },
    async fetchSummary() {
      try {
        const param = `q=${this.search}&status=${this.status}&start_date=${this.start_date}&end_date=${this.end_date}`;
        const { data } = await this.$http.get(`/get_orders_dash_summary?${param}`);
        const s = data && data.data || {};
        this.summaries = [
          { title: "orders", icon: "fal fa-list", value: Number(s.orders_count || 0) },
          { title: "cost", icon: "fal fa-coins", value: Number(s.sum_total_price_buy || 0) },
          { title: "price", icon: "fal fa-dollar-sign", value: Number(s.sum_total_price_sale || 0) },
          { title: "profit", icon: "fal fa-chart-line", value: Number(s.sum_total_profit || 0) },
        ];
      } catch (e) {
        console.log(e);
        this.summaries = [];
      }
    },
    onSearch() {
      this.SET_OVERLAY(true);
      setTimeout(() => {
        this.fetchItem();
        this.fetchSummary();
        this.SET_OVERLAY(false);
      }, 500);
    },
    async filterDate(start_date, end_date) {
      try {
        this.SET_OVERLAY(true);
        await Help.getDifferentMonth(this, start_date, end_date);
        setTimeout(() => {
          this.fetchItem();
          this.fetchSummary();
          this.SET_OVERLAY(false);
        }, 500);
      } catch (err) {
        console.log("error", err.message);
      }
    },
    viewItem({ id }) {
      this.$router.push(`/marketplace-orders/${id}`);
    },
    async exportOrders() {
      try {
        this.exporting = true;
        const url = `download_orders_dash_report?status=${this.status}&start_date=${this.start_date}&end_date=${this.end_date}&q=${this.search}`;
        const res = await this.$http.get(url, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        const fileName = `marketplace_orders_${this.start_date}_to_${this.end_date}.xlsx`;
        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast(this, "excel_export_success");
      } catch (e) {
        console.log(e);
      } finally {
        this.exporting = false;
      }
    },
  },
  watch: {
    status(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchItem();
        this.fetchSummary();
      }
    },
    start_date(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchItem();
        this.fetchSummary();
      }
    },
    end_date(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchItem();
        this.fetchSummary();
      }
    },
  },
};
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}

.ps-card-soft {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  transition: box-shadow 0.15s ease;
}

.ps-summary-icon {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.ps-products-title {
  font-size: 1.25rem;
  font-weight: 900;
  color: #141021;
}

</style>
